import { UserProfileCreateDto, UserProfilePatchDto, UserProfileSelectDto } from '@@types/api.types.ts';
import { useApi } from '@hooks/api.hook.tsx';
import { queryKeys } from '@queries/keys.ts';
import { referrerStepSchema } from '@schemas/user-profile.schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { removeEmptyStrings } from '@utils/form-validator.util.ts';

export const useSearchUserProfiles = (referralCode?: string) => {
  const { apiGet } = useApi();

  const result = referrerStepSchema.shape.referrerCode.safeParse(referralCode);
  const enabled = !!referralCode && result.success;

  return useQuery({
    queryKey: queryKeys.userProfile.searchUserProfiles(referralCode),
    queryFn: async () => {
      return await apiGet<UserProfileSelectDto[]>({
        path: '/v1/users/profiles/search',
        query: { referralCode },
      });
    },
    enabled,
  });
};

export const useSubmitUserProfile = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  const onSuccess = (data: UserProfileSelectDto) => {
    queryClient.setQueryData(queryKeys.userProfile.getUserProfile, data);
    queryClient.invalidateQueries({
      queryKey: queryKeys.users.getCurrentUser,
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.userProfile.getUserProfiles,
    });
  };

  return useMutation<UserProfileSelectDto, Error, UserProfileCreateDto>({
    mutationFn: async (formData) => {
      const cleanedData = removeEmptyStrings(formData);
      return await apiPost<UserProfileSelectDto>({
        path: '/v1/users/profiles',
        body: cleanedData,
      });
    },
    onSuccess,
  });
};

export const useUpdateUserProfile = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  const onSuccess = (data: UserProfileSelectDto) => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.users.getCurrentUser,
    });
  };

  return useMutation<UserProfileSelectDto, Error, UserProfilePatchDto>({
    mutationFn: async (formData) => {
      const cleanedData = removeEmptyStrings(formData);
      return await apiPatch<UserProfileSelectDto>({
        path: '/v1/users/profiles',
        body: cleanedData,
      });
    },
    onSuccess,
  });
};
